import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import {
  FiHome,
  FiMail,
  FiLock,
  FiEye,
  FiEyeOff,
  FiLogIn,
  FiAlertCircle,
  FiUserPlus
} from "react-icons/fi";
import { GoogleLogin } from "@react-oauth/google";   // 🔥 ADDED
import API_BASE_URL from "../config";
import { useAuth } from "../context/AuthContext";
import "../styles/Login.css";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const location = useLocation();

  const [form, setForm] = useState({
    email: "",
    password: ""
  });

  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  /* ==============================
     LOAD LOGO FROM SITE SETTINGS
  ============================== */
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/site-settings`);
        const data = await res.json();

        if (data?.logo && data?.logoContentType) {
          const base64 = `data:${data.logoContentType};base64,${data.logo}`;
          setLogoUrl(base64);
        }
      } catch (err) {
        console.error("Failed to load logo:", err);
      }
    };

    fetchLogo();
  }, []);

  /* ==============================
     🔵 NORMAL LOGIN HANDLER (UNCHANGED)
  ============================== */
  const handleLogin = async () => {
    if (loading) return;

    setLoading(true);
    setMessage("");

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });

      const data = await res.json();

      if (!res.ok) {
        setMessage(data.message || "Login failed");
        setLoading(false);
        return;
      }

      login(data.token, data.user);

      // Check if user came from booking
      const redirectTo = location.state?.redirectTo;
      const bookingState = location.state?.bookingState;

      if (redirectTo) {
        navigate(redirectTo, {
          replace: true,
          state: {
            ...bookingState,
            fromLoginRedirect: true   // 🔥 ADD THIS
          }
        });
        return;
      }

      // Default role-based redirect
      if (data.user.role === "ADMIN") {
        navigate("/admin/dashboard", { replace: true });
      } else if (data.user.role === "SOLICITOR") {
        navigate("/solicitor/dashboard", { replace: true });
      } else {
        navigate("/", { replace: true });
      }

    } catch (err) {
      console.error("Login error:", err);
      setMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ==============================
     🔵 GOOGLE LOGIN HANDLER (NEW)
  ============================== */
  const handleGoogleLogin = async (credentialResponse) => {
    try {
      setLoading(true);
      setMessage("");

      const res = await fetch(`${API_BASE_URL}/api/auth/google-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: credentialResponse.credential
        })
      });

      const data = await res.json();

      if (!res.ok) {
        setMessage(data.message || "Google login failed");
        setLoading(false);
        return;
      }

      // ✅ SAVE LOGIN
      login(data.token, data.user);

      // Check if user came from booking
      const redirectTo = location.state?.redirectTo;
      const bookingState = location.state?.bookingState;

      if (redirectTo) {
        navigate(redirectTo, {
          replace: true,
          state: {
            ...bookingState,
            fromLoginRedirect: true   // 🔥 ADD THIS
          }
        });
        return;
      }

      // Default role-based redirect
      if (data.user.role === "ADMIN") {
        navigate("/admin/dashboard", { replace: true });
      } else if (data.user.role === "SOLICITOR") {
        navigate("/solicitor/dashboard", { replace: true });
      } else {
        navigate("/", { replace: true });
      }

    } catch (err) {
      console.error("Google login error:", err);
      setMessage("Google login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ==============================
     HANDLE KEY PRESS
  ============================== */
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <div className="premium-login-page">

      {/* ==============================
         TOP RIGHT HOME BUTTON ONLY
      ============================== */}
      <div className="premium-login-home-container">
        <Link to="/" className="premium-login-home-btn">
          <FiHome size={20} />
          <span>Home</span>
        </Link>
      </div>

      {/* ==============================
         MAIN CONTENT
      ============================== */}
      <div className="premium-login-container">
        {/* Login Form */}
        <div className="premium-login-form-container">
          <div className="premium-login-form-card">
            {/* Logo above Welcome Back */}
            <div className="premium-login-form-logo">
              {logoUrl ? (
                <img src={logoUrl} alt="Brand Logo" className="premium-login-form-brand-logo" />
              ) : (
                <div className="premium-login-form-brand-text">Justivon</div>
              )}
            </div>

            <div className="premium-login-form-header">
              <h2 className="premium-login-form-title">Welcome Back</h2>
              <p className="premium-login-form-subtitle">
                Sign in to your account to continue
              </p>
            </div>

            {/* 🔵 GOOGLE LOGIN (REAL AUTH, SAME STYLE POSITION) */}
            <div className="premium-login-google-wrapper">
              <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={() => setMessage("Google login failed")}
                theme="outline"
                size="large"
                width="100%"
                shape="rectangular"
                text="continue_with"
                locale="en"
                useOneTap={false}
              />
            </div>

            {/* Divider */}
            <div className="premium-login-divider">
              <span>Or continue with</span>
            </div>

            {/* Error Message */}
            {message && (
              <div className="premium-login-error-message">
                <FiAlertCircle size={18} />
                <span>{message}</span>
              </div>
            )}

            {/* Email Field */}
            <div className="premium-login-form-group">
              <label className="premium-login-label">
                <FiMail size={16} />
                <span>Email Address</span>
              </label>
              <div className="premium-login-input-group">
                <input
                  className="premium-login-input"
                  type="email"
                  placeholder="Enter your email address"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  onKeyPress={handleKeyPress}
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="premium-login-form-group">
              <label className="premium-login-label">
                <FiLock size={16} />
                <span>Password</span>
              </label>
              <div className="premium-login-input-group premium-login-password-group">
                <input
                  className="premium-login-input"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  onKeyPress={handleKeyPress}
                />
                <button
                  className="premium-login-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  type="button"
                >
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
            </div>

            {/* Login Button */}
            <button
              className="premium-login-submit-btn"
              onClick={handleLogin}
              disabled={loading}
            >
              {loading ? (
                <div className="premium-login-spinner"></div>
              ) : (
                <FiLogIn size={18} />
              )}
              <span>{loading ? "Signing In..." : "Sign In"}</span>
            </button>

            {/* Forgot Password */}
            <div className="premium-login-forgot-center">
              <Link to="/forgot-password" className="premium-login-forgot-link">
                Forgot Password?
              </Link>
            </div>

            {/* Sign Up Link */}
            <div className="premium-login-signup-link">
              <span>Don't have an account?</span>
              <Link to="/signup" className="premium-login-signup-btn">
                <FiUserPlus size={16} />
                <span>Create Account</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
